import React, { useState } from 'react';
import type { IPlayerProps } from '@lottiefiles/react-lottie-player';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import {
  cssColorValue,
  cssSpacing,
  Tooltip,
  TooltipTrigger,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { useClipboard } from '@hooks/useClipboard';
import { buildAddSnackbarAction } from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import copyToGreenCheckAnimation from './copyToGreenCheckAnimation.json';

const LottiePlayer = dynamic<IPlayerProps>(
  () =>
    import('@lottiefiles/react-lottie-player').then(m => ({
      default: m.Player,
    })),
  { ssr: false },
);

const Container = styled.div<{
  hasHoverBackground: boolean;
}>`
  width: 36px;
  height: 36px;
  padding-top: 6px;
  border-radius: 50%;

  &:hover {
    background: ${props =>
      props.hasHoverBackground
        ? cssColorValue('backgroundTintedBase')
        : 'none'};
    cursor: pointer;
  }
`;

interface CopyToGreenCheckAnimationProps {
  hasHoverBackground: boolean;
  label: string;
  copyText: string;
  tooltipText?: string;
  snackbarText?: string;
  screenReaderText?: string;
  handleClick: () => void;
  withPortal?: boolean;
}

/*
This is the default copy icon that should be used in Atlas. By default, it will:
- render a provided text upon hover
- render a green check animation upon click
- render a snackbar of the provided text upon click

If you need different behavior, you should create and use another component.
*/
export const CopyToGreenCheckAnimation = ({
  hasHoverBackground = true,
  label,
  copyText,
  tooltipText,
  snackbarText,
  screenReaderText,
  handleClick,
  withPortal = false,
}: CopyToGreenCheckAnimationProps) => {
  const [showTooltip, toggleTooltip] = useState<boolean>(false);
  const [showAnimation, toggleShowAnimation] = useState<boolean>(false);

  const t = useT();
  const { copyText: copyTextToClipboard } = useClipboard(copyText || '');
  const [, dispatch] = useSnackbarNotifications();

  const COPIED_TO_CLIPBOARD = t(
    'atlas.snackbars.copied_to_clipboard',
    'Copied to clipboard',
    'Snackbar message to indicate copied to clipboard',
  );
  const COPY = t('atlas.actions.copy', 'Copy', 'To copy to clipboard');

  const handleCopyClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    handleClick();
    toggleShowAnimation(true);
    copyTextToClipboard();
    dispatch(buildAddSnackbarAction(snackbarText || COPIED_TO_CLIPBOARD));
  };

  return (
    <TooltipTrigger
      overlay={showTooltip && <Tooltip>{tooltipText || COPY}</Tooltip>}
      onShow={() => toggleTooltip(true)}
      onHide={() => toggleTooltip(false)}
      placement={TooltipTrigger.top}
      withPortal={withPortal}
    >
      <Container
        onClick={handleCopyClick}
        hasHoverBackground={hasHoverBackground}
        aria-label={label}
      >
        <LottiePlayer
          key={showAnimation ? 'playing' : 'idle'}
          autoplay={showAnimation}
          src={copyToGreenCheckAnimation}
          style={{
            height: `${cssSpacing('base', '24px')}`,
            width: `${cssSpacing('base', '24px')}`,
          }}
          onEvent={(event: string) => {
            if (event === 'complete') {
              toggleShowAnimation(false);
            }
          }}
        />
        <VisuallyHidden>{screenReaderText || COPY}</VisuallyHidden>
      </Container>
    </TooltipTrigger>
  );
};
